import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ChevronLeft, Sparkles, Store, Trophy, Gift, Loader2, History, Zap, ArrowRight,
} from "lucide-react";

export default function MyLoyalty() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/loyalty");
      setWallets(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load rewards");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const totalLifetime = useMemo(() => wallets.reduce((n, w) => n + (w.lifetime_points || 0), 0), [wallets]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-24 space-y-4" data-testid="my-loyalty-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="loyalty-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <header className="rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/15 via-zinc-950 to-pink-500/10 p-5">
        <p className="inline-flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-[0.3em] font-black">
          <Trophy className="w-3 h-3" /> My Rewards
        </p>
        <h1 className="text-3xl font-black tracking-tighter mt-1">Loyalty points</h1>
        <p className="text-sm text-zinc-400 mt-1">Each shop runs its own program. Points earned with one vendor can only be used at that same vendor.</p>
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-100 font-black text-sm" data-testid="loyalty-lifetime">
          <Sparkles className="w-3.5 h-3.5" /> Lifetime {totalLifetime.toLocaleString()} pts across {wallets.length} shop{wallets.length === 1 ? "" : "s"}
        </div>
      </header>

      {loading ? (
        <div className="py-6 text-center text-zinc-500 text-sm inline-flex items-center gap-2 justify-center w-full"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : wallets.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-10 text-center text-zinc-400 text-sm" data-testid="loyalty-empty">
          <Gift className="w-10 h-10 mx-auto text-amber-400 mb-3" />
          <p className="font-black text-white text-base mb-1">No rewards yet</p>
          <p>Order from any Hidden Gem shop to start earning points automatically.</p>
          <button onClick={() => navigate("/feed")} className="mt-4 h-11 px-5 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-bold inline-flex items-center gap-2" data-testid="loyalty-browse">
            <Zap className="w-4 h-4" /> Browse the feed
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {wallets.map((w) => (
            <button
              key={w.shop_id}
              onClick={() => navigate(`/me/loyalty/${w.shop_id}`)}
              className="w-full flex items-center gap-3 rounded-2xl bg-white/[0.03] border border-white/10 p-4 hover:border-amber-400/50 transition-colors text-left"
              data-testid={`loyalty-shop-${w.shop_id}`}
            >
              <div className="w-12 h-12 rounded-2xl overflow-hidden bg-white/5 border border-white/10 shrink-0">
                {w.shop?.logo_url ? <img src={fileUrl(w.shop.logo_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><Store className="w-5 h-5 text-zinc-400" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-black tracking-tight truncate">{w.shop?.name || w.shop_id}</p>
                <p className="text-[11px] text-zinc-400 truncate">
                  {w.tier?.label ? <><span className="text-amber-300 font-black">{w.tier.label}</span> · </> : null}
                  Lifetime {w.lifetime_points?.toLocaleString() || 0} pts
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xl font-black text-amber-300 tabular-nums">{w.points?.toLocaleString() || 0}</p>
                <p className="text-[10px] text-zinc-500 uppercase tracking-widest">available</p>
              </div>
              <ArrowRight className="w-4 h-4 text-zinc-500 shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
